import React from 'react';

export interface GraphemeLimitPolicy {
  maxGraphemes: number;
  onReject?: (args: {text: string; maxGraphemes: number}) => void;
}

export const GraphemeLimitContext = React.createContext<GraphemeLimitPolicy | null>(null);

/**
 * Opt-in editing policy that limits the total number of graphemes that can be contained in an
 * EditableComponent. Each EditableComponent has its own budget.
 *
 * Pastes routed through an EditableComponent's custom `pasteHandler` bypass the limit, so editors
 * with a custom paste handler can exceed `maxGraphemes`. Editors with `allowHTML` are not supported:
 * their text is raw markup, so the limit would count tags rather than visible text.
 */
export default function GraphemeLimitBoundary({
  maxGraphemes = 500,
  onReject,
  children,
}: Partial<GraphemeLimitPolicy> & {children: React.ReactNode}): JSX.Element {
  const policy = React.useMemo(() => ({maxGraphemes, onReject}), [maxGraphemes, onReject]);
  return <GraphemeLimitContext.Provider value={policy}>{children}</GraphemeLimitContext.Provider>;
}
